import OriginTracer from '@atlassiansox/origin-tracing';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import { inviteWithinOrg } from '@atlassian/invite-prompt';

import {
	fireInviteFailed,
	fireInviteRequested,
	fireInviteSucceeded,
	fireProductAccessRequested,
	fireUserInvited,
	getExperimentAnalytics,
} from '../analytics';

import type { BulkInviteSuccessResponse, InviteUsersRequest } from './data-types';
import { getSiteAri, handleInviteUsersSuccessResponse } from './utilities';

export const inviteUsers = async (
	{ userIds, continueUrl, tenantId, product }: InviteUsersRequest,
	createAnalyticsEvent: CreateUIAnalyticsEvent,
): Promise<BulkInviteSuccessResponse> => {
	try {
		const origin = new OriginTracer({ product: 'confluence' });
		const continueUrlWithOrigin = origin.addToUrl(continueUrl);
		const originAttributes = origin.toAnalyticsAttributes({
			hasGeneratedId: true,
		});
		const inviteApiResponse = await inviteWithinOrg(
			userIds,
			continueUrlWithOrigin,
			tenantId,
			product,
		);

		const response = handleInviteUsersSuccessResponse(inviteApiResponse);

		[...response.invited, ...response.accessRequested].forEach((invited) => {
			const resourceAri = getSiteAri(tenantId, product);
			const result = invited.results[resourceAri];
			const userId = invited.id;
			const analyticsAttributes = {
				...originAttributes,
				userId,
				products: [product],
				inviteResult: [
					{
						resourceAri,
						status: result,
					},
				],
				integration: 'inviteOnPagePublish',
			};
			const analytics = getExperimentAnalytics(createAnalyticsEvent, {
				attributes: analyticsAttributes,
			});
			if (result === 'INVITED') {
				fireUserInvited(analytics, userId);
			} else if (result === 'INVITED_PENDING_APPROVAL') {
				fireProductAccessRequested(analytics, userId);
			} else {
				fireInviteRequested(analytics);
			}
		});

		fireInviteSucceeded(getExperimentAnalytics(createAnalyticsEvent));

		return response;
	} catch (error: any) {
		fireInviteFailed(getExperimentAnalytics(createAnalyticsEvent), error);
		throw error;
	}
};
